/**
 * CloudEvents Protocol Parser
 *
 * Parses CNCF CloudEvents 1.0 format messages.
 * Supports both JSON and binary content modes.
 */

import type { IProtocolParser, ParsedMessage } from '../types';
import { createGuid } from '../utils/idGenerator';

interface CloudEvent {
  specversion: string;
  type: string;
  source: string;
  id: string;
  [key: string]: unknown;
}

export class CloudEventsParser implements IProtocolParser {
  readonly name = 'cloudevents';

  canParse(raw: string | ArrayBuffer): boolean {
    try {
      const text = typeof raw === 'string' ? raw : new TextDecoder().decode(new Uint8Array(raw));
      const trimmed = text.trim();
      if (!trimmed.startsWith('{')) {
        return false;
      }
      const obj = JSON.parse(text) as Record<string, unknown>;
      return (
        typeof obj.specversion === 'string' &&
        typeof obj.type === 'string' &&
        typeof obj.source === 'string' &&
        typeof obj.id === 'string'
      );
    } catch {
      return false;
    }
  }

  parse(raw: string | ArrayBuffer): ParsedMessage {
    try {
      const text = typeof raw === 'string' ? raw : new TextDecoder().decode(new Uint8Array(raw));
      const event = JSON.parse(text) as CloudEvent;

      // Validate CloudEvents required fields
      if (!event.specversion || !event.type || !event.source || !event.id) {
        return {
          body: null,
          format: 'cloudevents',
          error: 'Missing required CloudEvents fields',
        };
      }

      return {
        body: event,
        format: 'cloudevents',
      };
    } catch (error) {
      return {
        body: null,
        format: 'cloudevents',
        error: `CloudEvents parse error: ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  }

  serialize(body: unknown): string {
    const event = body as Partial<CloudEvent>;
    const cloudevent: CloudEvent = {
      specversion: event.specversion || '1.0',
      type: event.type || 'com.example.unknown',
      source: event.source || 'https://example.com',
      id: event.id || createGuid(),
      ...event,
    };
    return JSON.stringify(cloudevent);
  }
}
